export type TemplateStatus =
  'APPROVED' | 'PENDING' | 'REJECTED' | 'PAUSED' | 'DISABLED' | 'IN_APPEAL';

export type TemplateCategory = 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';

export type TemplateRejectedReason =
  | 'ABUSIVE_CONTENT'
  | 'INCORRECT_CATEGORY'
  | 'INVALID_FORMAT'
  | 'PROMOTIONAL'
  | 'SCAM'
  | 'TAG_CONTENT_MISMATCH';

export type TemplateComponentType = 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';

export interface TemplateButton {
  type?: string;
  text: string;
  url?: string;
  phone_number?: string;
  [key: string]: unknown;
}

export interface TemplateComponent {
  type: TemplateComponentType | string;
  format?: string;
  text?: string;
  buttons?: TemplateButton[];
  example?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface Template {
  id: string;
  name: string;
  language: string;
  category: TemplateCategory;
  status: TemplateStatus;
  components: TemplateComponent[];
  rejectedReason?: TemplateRejectedReason | string | null;
  ownerRoles?: string[];
}

export interface ListTemplatesOptions {
  status?: TemplateStatus | null;
  cursor?: string | null;
  limit?: number;
}

export interface CreateTemplateProps {
  name: string;
  category: TemplateCategory;
  language: string;
  components: TemplateComponent[];
}

export interface CreatedTemplate {
  id: string;
  name: string;
  language: string;
  status: TemplateStatus;
  ownerRoles?: string[];
}

export interface DeleteTemplateProps {
  name: string;
  language?: string | null;
}

export interface TemplateVariable {
  token: string;
  name: string;
}

export function findComponent(
  template: Template,
  type: TemplateComponentType
): TemplateComponent | undefined {
  return template.components?.find(component => component.type === type);
}

export function templateBody(template: Template): string {
  return findComponent(template, 'BODY')?.text ?? '';
}

export function templateHeader(template: Template): string {
  const header = findComponent(template, 'HEADER');
  return header?.format === 'TEXT' ? (header.text ?? '') : '';
}

export function templateFooter(template: Template): string {
  return findComponent(template, 'FOOTER')?.text ?? '';
}

export function templateButtons(template: Template): TemplateButton[] {
  return findComponent(template, 'BUTTONS')?.buttons ?? [];
}

export function templateRejectedReason(template: Template): TemplateRejectedReason | string | null {
  return template.status === 'REJECTED' ? (template.rejectedReason ?? null) : null;
}

export const TEMPLATE_VARIABLE_NAME = /^[a-z][a-z0-9_]*$/;

const VARIABLE_TOKEN = /\{\{([a-z][a-z0-9_]*|\d+)\}\}/g;

export function templateVariables(template: Template): TemplateVariable[] {
  const body = templateBody(template);
  const found = new Set<string>();

  for (const match of body.matchAll(VARIABLE_TOKEN)) {
    found.add(match[1]);
  }

  const names = [...found];

  // Templates antigos usam variáveis posicionais: a ordem é a numérica, não a de aparição.
  if (names.every(name => /^\d+$/.test(name))) {
    names.sort((left, right) => Number(left) - Number(right));
  }

  return names.map(name => ({ name, token: `{{${name}}}` }));
}

export function templateExampleValues(template: Template): string[] {
  const example = findComponent(template, 'BODY')?.example;
  const rows = example?.['body_text'];

  if (Array.isArray(rows) && Array.isArray(rows[0])) {
    return (rows[0] as unknown[]).map(value => (typeof value === 'string' ? value.trim() : ''));
  }

  const named = example?.['body_text_named_params'];

  if (!Array.isArray(named)) {
    return [];
  }

  return templateVariables(template).map(variable => {
    const entry = named.find(
      value =>
        typeof value === 'object' &&
        value !== null &&
        (value as Record<string, unknown>)['param_name'] === variable.name
    ) as Record<string, unknown> | undefined;
    const value = entry?.['example'];

    return typeof value === 'string' ? value.trim() : '';
  });
}

export function templateExampleVariables(template: Template): Record<string, string> {
  const values = templateExampleValues(template);
  const variables: Record<string, string> = {};

  templateVariables(template).forEach(({ name }, position) => {
    const value = values[position];

    if (value) {
      variables[name] = value;
    }
  });

  return variables;
}

export function renderTemplateBody(template: Template, variables: Record<string, string>): string {
  return templateBody(template).replace(VARIABLE_TOKEN, (token, name: string) => {
    const value = variables[name];

    return value && value.trim() ? value : token;
  });
}
